import React, { useEffect, useState } from 'react';
import styles from '../styles/views/ApiV2Docs.module.scss';

function ApiV2Docs() {
  const [activeSection, setActiveSection] = useState('system-overview');

  // Smooth scroll for same-page anchors
  useEffect(() => {
    const handler = (e: Event) => {
      const target = e.currentTarget as HTMLAnchorElement;
      const href = target.getAttribute('href');
      if (!href || !href.startsWith('#')) return;
      e.preventDefault();
      const el = document.querySelector(href);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        const sectionId = href.substring(1);
        setActiveSection(sectionId);
      }
    };

    const links = Array.from(document.querySelectorAll('a[href^="#"]')) as HTMLAnchorElement[];
    links.forEach((a) => a.addEventListener('click', handler));
    return () => links.forEach((a) => a.removeEventListener('click', handler));
  }, []);

  return (
    <div className={styles.standaloneLayout}>
      <header className={styles.pageHeader}>
        <div className={styles.headerContent}>
          <h1 className={styles.pageTitle}>API v2 Documentation</h1>
          <p className={styles.pageDescription}>
            Cross-border payments through an API-driven USD stablecoin credit system
          </p>
        </div>
      </header>
      
      <div className={styles.apiV2Docs}>
        {/* Sidebar navigation */}
        <aside className={styles.sidebar}>
          <nav className={styles.sidebarNav} aria-label="API sections">
            <div className={styles.navSection}>
              <h3 className={styles.navSectionTitle}>OVERVIEW</h3>
              <ul className={styles.navList}>
                <li>
                  <a 
                    href="#system-overview" 
                    className={activeSection === 'system-overview' ? styles.active : ''}
                  >
                    System Overview
                  </a>
                </li>
                <li>
                  <a 
                    href="#system-components" 
                    className={activeSection === 'system-components' ? styles.active : ''}
                  >
                    System Components
                  </a>
                </li>
                <li>
                  <a 
                    href="#system-architecture" 
                    className={activeSection === 'system-architecture' ? styles.active : ''}
                  >
                    System Architecture
                  </a>
                </li>
              </ul>
            </div>

            <div className={styles.navSection}>
              <h3 className={styles.navSectionTitle}>PAYMENT FLOWS</h3>
              <ul className={styles.navList}>
                <li>
                  <a 
                    href="#payouts" 
                    className={activeSection === 'payouts' ? styles.active : ''}
                  >
                    Payouts
                  </a>
                </li>
                <li>
                  <a 
                    href="#payins" 
                    className={activeSection === 'payins' ? styles.active : ''}
                  >
                    Payins
                  </a>
                </li>
              </ul>
            </div>

            <div className={styles.navSection}>
              <h3 className={styles.navSectionTitle}>FUNCTIONS</h3>
              <ul className={styles.navList}>
                <li>
                  <a 
                    href="#balances" 
                    className={activeSection === 'balances' ? styles.active : ''}
                  >
                    Balances
                  </a>
                </li>
                <li>
                  <a 
                    href="#wallet-compliance" 
                    className={activeSection === 'wallet-compliance' ? styles.active : ''}
                  >
                    Wallet Compliance
                  </a>
                </li>
              </ul>
            </div>
          </nav>
        </aside>

        <main className={styles.mainContent}>
          {/* System Overview */}
          <section id="system-overview" className={styles.section}>
            <h2 className={styles.sectionTitle}>System Overview</h2>
            <div className={styles.sectionContent}>
              <p>
                Liquid Credit powers cross-border payments through an API-driven USD stablecoin credit system with instant settlement, automated repayment, automatic currency conversion and compliance management. Each FinTech is issued a dedicated Credit Wallet. All stablecoin inflows are routed through this regulated wallet, which functions as a repayment wallet, automatically paying down the credit line and recycling capital in real time.
              </p>
              <p>
                Liquid Credit APIs support multiple payment flows: payouts, pay-ins, so partners can embed stablecoin credit exactly where liquidity is needed. The API manages balances and payout and payin transactions through wallet architecture that enables seamless flows between USD stablecoins and fiat currencies.
              </p>

              <h3>Key functionality:</h3>
              <ol>
                <li><strong>Real-time Operations:</strong> Immediate processing and balance updates</li>
                <li><strong>Multi-currency Flexibility:</strong> Seamless conversion between USD stablecoins and fiat conversion</li>
                <li><strong>Compliance Built-in:</strong> Automated screening and verification</li>
                <li><strong>Scalable Architecture:</strong> High-throughput REST API</li>
                <li><strong>Risk Management:</strong> Collateral backing and balance monitoring</li>
              </ol>
            </div>
          </section>

          {/* System Components */}
          <section id="system-components" className={styles.section}>
            <h2 className={styles.sectionTitle}>System Components</h2>
            <div className={styles.sectionContent}>
              <div className={styles.componentBlock}>
                <h3>USDC Capital Pool</h3>
                <p><strong>Purpose:</strong> Debt capital that provides USD stablecoin liquidity to the Credit Wallet</p>
                <p><strong>Currency:</strong> USD stablecoins</p>
              </div>

              <div className={styles.componentBlock}>
                <h3>Credit Wallet</h3>
                <p><strong>Purpose:</strong> Multi-chain wallet assigned to each FinTech, which facilitates the draw down of USD stablecoin credit and receives repayments</p>
                <p><strong>Multiple addresses:</strong> FinTechs have the ability to set up more than one wallet address to receive repayments</p>
                <p><strong>Currency:</strong> USD stablecoins</p>
              </div>

              <div className={styles.componentBlock}>
                <h3>Customer Wallets</h3>
                <p><strong>Purpose:</strong> Wallets that interact with the Credit Wallet for transaction settlement. All wallets from Sender customer and Receiver customer must be whitelisted and compliance checked.</p>
                <p><strong>Receiver Wallet:</strong> Receives USD stablecoin credit from the Credit Wallet for a payout transaction</p>
                <p><strong>Sender Wallet:</strong> Initiates payin transaction and sends USD stablecoins to the Credit Wallet</p>
                <p><strong>Currency:</strong> USD stablecoins</p>
              </div>

              <div className={styles.componentBlock}>
                <h3>Fiat Collateral Wallet</h3>
                <p><strong>Purpose:</strong> Multi-chain wallet that facilitates the conversion between non-USD fiat and non-USD stablecoins to be used to access USD stablecoin credit for payout transactions and settle payin transactions with non-USD fiat</p>
                <p><strong>BlockRadar Integration:</strong> Enables the conversion between naira and cNGN within payout and payin transactions</p>
                <p><strong>Currency:</strong> non-USD stablecoins</p>
              </div>

              <div className={styles.infoPanel}>
                <h4>Multi-Stablecoin Support</h4>
                <ul>
                  <li><strong>Credit Line:</strong> USDC, USDT</li>
                  <li><strong>Fiat collateral:</strong> cNGN</li>
                  <li><strong>Blockchains:</strong> Base, Ethereum and others</li>
                </ul>
              </div>
            </div>
          </section>

          {/* System Architecture */}
          <section id="system-architecture" className={styles.section}>
            <h2 className={styles.sectionTitle}>System Architecture</h2>
            <div className={styles.sectionContent}>
              <div className={styles.diagramBlock}>
                <object
                  data="/docs/api/flow-diagram.svg"
                  type="image/svg+xml"
                  className={styles.diagram}
                  aria-label="Liquid Credit System Flow"
                >
                  <img src="/docs/api/flow-diagram.svg" alt="Liquid Credit System Flow" className={styles.diagram} />
                </object>
              </div>

              <div className={styles.infoPanel}>
                <h4>API Architecture</h4>
                <ul>
                  <li><strong>RESTful:</strong> Standard HTTP methods and status codes</li>
                  <li><strong>JSON:</strong> Structured payloads</li>
                  <li><strong>Real-time:</strong> Immediate balance reflection</li>
                  <li><strong>Error Handling:</strong> Clear validation and error responses</li>
                </ul>
              </div>
            </div>
          </section>

          {/* Payouts */}
          <section id="payouts" className={styles.section}>
            <h2 className={styles.sectionTitle}>Payouts</h2>
            <div className={styles.sectionContent}>
              <p className={styles.sectionIntro}>Funds are disbursed and settled with USD stablecoin credit</p>

              <div className={styles.useCaseBlock}>
                <h3>Use Case 1: Payout to USD stablecoin</h3>
                <p><strong>Action:</strong> Send USD stablecoin credit to Customer Wallet</p>
                <h4>Process:</h4>
                <ul>
                  <li>Draw down USD stablecoin credit to Receiver Wallet</li>
                  <li>Available balance decreases</li>
                  <li>Credit outstanding balance increases</li>
                  <li>Receiver Wallet receives USD stablecoins</li>
                </ul>
              </div>

              <div className={styles.useCaseBlock}>
                <h3>Use Case 2: Payout to USD</h3>
                <p><strong>Action:</strong> Send USD stablecoin credit to USD bank account</p>
                <h4>Process:</h4>
                <ul>
                  <li>Draw down USD stablecoin credit to USD bank account</li>
                  <li>Available balance decreases</li>
                  <li>Credit outstanding balance increases</li>
                  <li>Payment partner on Liquid Credit platform receives USD stablecoins</li>
                  <li>Payment partner on Liquid Credit platform converts USD stablecoins to USD into bank account</li>
                </ul>
              </div>

              <div className={styles.useCaseBlock}>
                <h3>Use Case 3: Payout from non-USD fiat to USD stablecoin</h3>
                <p><strong>Action:</strong> Provide non-USD fiat and send USD stablecoin credit to Customer Wallet</p>
                <h4>Process:</h4>
                <ul>
                  <li>Receives non-USD fiat and converts to non-USD stablecoins (1:1)</li>
                  <li>Fiat Collateral Wallet balance increases with non-USD stablecoins</li>
                  <li>Draw down USD stablecoin credit (at specified exchange rate) to Receiver Wallet</li>
                  <li>Available balance decreases</li>
                  <li>Credit outstanding balance increases</li>
                  <li>Receiver Wallet receives USD stablecoins</li>
                </ul>
              </div>
            </div>
          </section>

          {/* Payins */}
          <section id="payins" className={styles.section}>
            <h2 className={styles.sectionTitle}>Payins</h2>
            <div className={styles.sectionContent}>
              <p className={styles.sectionIntro}>USD stablecoins are sent to repay USD stablecoin credit</p>

              <div className={styles.useCaseBlock}>
                <h3>Use Case 1: Payin with USD stablecoin</h3>
                <p><strong>Action:</strong> Receive USD stablecoins into Credit Wallet</p>
                <h4>Process:</h4>
                <ul>
                  <li>Sender Wallet transfers USD stablecoins to Credit Wallet</li>
                  <li>Available balance increases</li>
                  <li>Credit outstanding balance decreases</li>
                  <li>Credit Wallet receives USD stablecoins</li>
                </ul>
              </div>

              <div className={styles.useCaseBlock}>
                <h3>Use Case 2: Payin with USD</h3>
                <p><strong>Action:</strong> Receive USD and onramp into USD stablecoins into Credit Wallet</p>
                <h4>Process:</h4>
                <ul>
                  <li>Payment partner on Liquid Credit platform receives USD</li>
                  <li>Payment partner on Liquid Credit platform converts USD to USD stablecoins sent to Credit Wallet</li>
                  <li>Credit Wallet receives USD stablecoins</li>
                  <li>Available balance increases</li>
                  <li>Credit outstanding balance decreases</li>
                </ul>
              </div>

              <div className={styles.useCaseBlock}>
                <h3>Use Case 3: Payin with USD stablecoin in return for non-USD fiat</h3>
                <p><strong>Action:</strong> Receive USD stablecoins into Credit Wallet and send non-USD fiat</p>
                <h4>Process:</h4>
                <ul>
                  <li>Sender Wallet transfers USD stablecoins to Credit Wallet</li>
                  <li>Available balance increases</li>
                  <li>Credit outstanding balance decreases</li>
                  <li>Credit Wallet receives USD stablecoins</li>
                  <li>Non-USD stablecoins available in the Fiat Collateral Wallet converts to non-USD fiat (1:1)</li>
                  <li>Fiat Collateral Wallet balance decreases with non-USD stablecoins (at specified exchange rate)</li>
                </ul>
              </div>
            </div>
          </section>

          {/* Balances */}
          <section id="balances" className={styles.section}>
            <h2 className={styles.sectionTitle}>Balances</h2>
            <div className={styles.sectionContent}>
              <p className={styles.sectionIntro}>Real-time balance monitoring for credit line and fiat collateral tracking</p>

              <p>
                The Balance Management API provides real-time visibility into your credit line status and collateral positions. 
                This enables you to monitor available liquidity, track credit utilization, and manage your fiat collateral 
                backing across multiple blockchain networks.
              </p>

              <div className={styles.infoPanel}>
                <h4>Key Metrics</h4>
                <ul>
                  <li><strong>Available Balance:</strong> USD stablecoins ready for immediate use</li>
                  <li><strong>Credit Outstanding Balance:</strong> USD stablecoins drawn down or borrowed</li>
                  <li><strong>Fiat Collateral Balance:</strong> Non-USD stablecoins securing the credit line</li>
                  <li><strong>Network:</strong> Blockchain network such as Base, Ethereum</li>
                </ul>
              </div>

              <h3>Balance Tracking Features</h3>
              <ul>
                <li><strong>Real-time Updates:</strong> Balances reflect immediately after each transaction</li>
                <li><strong>Multi-Currency Support:</strong> Track balances across USDC, USDT, and collateral stablecoins</li>
                <li><strong>Network Visibility:</strong> View balances per blockchain network</li>
                <li><strong>Credit Line Monitoring:</strong> Track available credit and outstanding amounts</li>
                <li><strong>Collateral Management:</strong> Monitor fiat collateral positions for currency conversion operations</li>
              </ul>
            </div>
          </section>

          {/* Wallet Compliance */}
          <section id="wallet-compliance" className={styles.section}>
            <h2 className={styles.sectionTitle}>Wallet Compliance</h2>
            <div className={styles.sectionContent}>
              <p className={styles.sectionIntro}>Comprehensive wallet whitelisting and compliance management for sender and receiver wallets</p>

              <p>
                The Wallet Compliance system ensures that all transactions are conducted with verified and compliant wallet 
                addresses. This includes automated screening, KYC/KYB verification, and continuous monitoring to maintain 
                regulatory compliance and security standards.
              </p>

              <h3>Wallet Whitelisting</h3>
              <p>
                All customer wallets must be added to the whitelist before they can participate in transactions. The 
                whitelisting process triggers automated compliance screening to ensure wallet addresses meet regulatory 
                requirements and security standards.
              </p>

              <h3>Compliance Features</h3>
              <ul>
                <li><strong>Automated Screening:</strong> All wallets are automatically screened against risk databases</li>
                <li><strong>KYC/KYB Requirements:</strong> Source and destination wallets require Know Your Customer/Business verification</li>
                <li><strong>Risk Assessment:</strong> Wallets are evaluated with severity levels (HIGH, MEDIUM, LOW)</li>
                <li><strong>Fraud Detection:</strong> Continuous monitoring for fraudulent activity</li>
                <li><strong>Compliance Status:</strong> Real-time compliance status (COMPLIANT/NOT_COMPLIANT)</li>
                <li><strong>Detailed Reports:</strong> Access to comprehensive compliance check results</li>
              </ul>

              <h3>Wallet Management Operations</h3>
              <ul>
                <li><strong>Add Wallets:</strong> Whitelist new wallet addresses with automatic compliance screening</li>
                <li><strong>View Wallets:</strong> Retrieve all whitelisted wallets with their compliance status</li>
                <li><strong>Remove Wallets:</strong> Remove wallet addresses from the whitelist when needed</li>
                <li><strong>Status Monitoring:</strong> Track compliance status changes and check results</li>
              </ul>

              <div className={styles.infoPanel}>
                <h4>Security Requirements</h4>
                <p>
                  All wallets require KYC/KYB verification and must pass automated compliance screening before they can 
                  be used in transactions. This ensures regulatory compliance and protects against fraud and illicit activities.
                </p>
              </div>
            </div>
          </section>
        </main>
      </div>
    </div>
  );
}

export default ApiV2Docs;
